import { ReactNode, useMemo } from 'react';
import { Alert as CurrentAlert, AlertProps as CurrentAlertProps } from '../current/Alert';
import { HeadingComponent } from '../..';

export type AlertVariation = 'info' | 'warning' | 'error' | 'success';

/**
 * @deprecated Legacy alert props are now deprecated. Please use new Alert props.
 */
export interface LegacyAlertProps extends IntrinsicProps {
  /** The id. */
  id?: string;
  /** Optional class name to appy to alert element - allows for style extension */
  className?: string;
  /** The heading text */
  heading?: string;
  /**
   * Add a custom element to the end of the heading.
   * @deprecated Legacy alert props are now deprecated. `HeadingElement` can be passed in directly to the `heading` prop.
   */
  headingElement?: JSX.Element;
  /** Event fired when the alert is closed. */
  onClose?: () => void;
  /**
   * The style variation of the alert. One of "info" (default), "warning", "error", "success"
   *
   * @deprecated Legacy alert props are now deprecated. `variation` prop name has been changed to `variant`. `variant` should now be used.
   */
  variation?: AlertVariation;
  /**
   * Determine if alert has text or not
   * @deprecated Legacy alert props are now deprecated. Please use new Alert props. `hasChild` is no longer supported and will be ignored.
   */
  hasChild?: boolean;
  /**
   * If alert mini has variation
   * @deprecated Legacy alert props are now deprecated. There is no longer a mini variation. Either use `heading` only or `heading` and `description`.
   */
  mini?: boolean;
  /**
   * Max-Width for alert
   * @deprecated Legacy alert props are now deprecated. Alerts now take up the full width of the parent's container.
   */
  maxWidth?: string;
  /**
   * Max-Width for alert
   * @deprecated Legacy alert props are now deprecated. Children should now get passed directly to the `description` prop.
   */
  children?: string | ReactNode;
}

export type AlertProps = CurrentAlertProps | LegacyAlertProps;

const HeadingElementWithCustom = ({ heading, headingElement }) => {
  return (
    <div>
      <HeadingComponent variation="H3">{heading}</HeadingComponent>
      <div>{headingElement}</div>
    </div>
  );
};

const mapLegacyToCurrent = (props: LegacyAlertProps): CurrentAlertProps => {
  let heading: string | ReactNode = props.heading;
  if (props.headingElement) {
    if (props.heading) {
      heading = <HeadingElementWithCustom heading={props.heading} headingElement={props.headingElement} />;
    } else {
      heading = props.headingElement;
    }
  }

  return {
    ...props,
    heading,
    description: props.children ?? (props as CurrentAlertProps).description,
    variant: props.variation ?? (props as CurrentAlertProps).variant,
    onClose: props.onClose,
    minWidth: null,
    className: props.className,
  };
};

// This is an adapter class for alerts using legacy Alert's props. The legacy props are converted to the current alert's props.
export const Alert = (props: AlertProps) => {
  props = useMemo(() => mapLegacyToCurrent(props as LegacyAlertProps), [props]);
  return <CurrentAlert {...(props as CurrentAlertProps)} />;
};
